import { Box, Theme } from '@mui/material';
import { FC, ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { Scrollbars } from 'react-custom-scrollbars-2';
import { createUseStyles } from 'react-jss';
import Text from '../text';
import ResizeBox from '../resize-box';
import { debounce } from 'lodash';

interface Props {
  children: ReactNode[];
  max?: number;
  emptyMessage?: string;
}

interface ScrollbarsWithUpdate extends Scrollbars {
  update: () => void;
}

const FLEX_GAP = 2;
const SPACER_HEIGHT = 4;

const useStyles = createUseStyles<string, { hasScrollbars: boolean }>((theme: Theme) => ({
  scrollBox: {
    width: '100%',
    '& div::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
      '-webkit-box-shadow': 'none',
    },
    '& .scrollbox-child': {
      width: '100%',
    },
  },
  scrollView: {
    display: 'flex',
    flexDirection: 'column',
    gap: FLEX_GAP,
    width: '100%',
    scrollbarWidth: 'thin',
    scrollbarColor: `${theme.palette.lightGray} transparent`,
    paddingRight: ({ hasScrollbars }) => (hasScrollbars ? 16 : 0),
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#ffffff35',
      outline: 'none',
      borderRadius: theme.shape.borderRadius,
    },
    '&::-webkit-scrollbar': {
      width: 10,
      backgroundColor: 'transparent',
    },
  },
  scrollThumbVert: {
    backgroundColor: '#00000045',
    borderRadius: theme.shape.borderRadius,
  },
  scrollHideHorizontal: {
    display: 'none',
  },
  emptyMessage: {
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.subtleGray,
  },
}));

const ScrollBox: FC<Props> = ({ children, max = 3, emptyMessage = '' }) => {
  const hasScrollbars = children.length > max;
  const styles = useStyles({ hasScrollbars });
  const ref = useRef<HTMLDivElement>(null);
  const scrollbarRef = useRef<ScrollbarsWithUpdate>(null);
  const [loaded, setLoaded] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const scrollBox = ref.current;
    if (!scrollBox || !isVisible || loaded) {
      return;
    }

    const visibleCount = Math.min(max, children.length);
    const scrollChildren = scrollBox.querySelectorAll('.scrollbox-child');

    const childHeight = Array.from(scrollChildren)
      .slice(0, visibleCount)
      .reduce((totalHeight: number, child: HTMLElement) => {
        return totalHeight + FLEX_GAP + Number(child?.offsetHeight || 0);
      }, 0);

    scrollBox.style.height = `${childHeight + SPACER_HEIGHT}px`;
    setLoaded(true);
  }, [max, children.length, isVisible, loaded]);

  // Track when the ScrollBox becomes visible
  useEffect(() => {
    if (!ref.current) {
      return;
    }

    const observer = new IntersectionObserver(
      ([{ isIntersecting, intersectionRatio }]) => {
        setTimeout(() => {
          setIsVisible(isIntersecting && intersectionRatio === 1);
        }, 100);
      },
      { threshold: 1, root: null }
    );

    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const showEmptyMessage = !children.length && !!emptyMessage;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const handleResize = useCallback(
    debounce(() => {
      scrollbarRef.current?.update();
    }, 50),
    []
  );

  return (
    <>
      <Box className={styles.scrollBox} ref={ref}>
        <Scrollbars
          renderView={(props) => <div {...props} className={styles.scrollView} />}
          renderThumbVertical={(props) => <div {...props} className={styles.scrollThumbVert}></div>}
          renderTrackHorizontal={(props) => (
            <div {...props} className={styles.scrollHideHorizontal}></div>
          )}
          renderThumbHorizontal={(props) => (
            <div {...props} className={styles.scrollHideHorizontal}></div>
          )}
          autoHide={false}
          ref={scrollbarRef}
        >
          {children.map((child, index) => {
            if (index >= max && !loaded) {
              return null;
            }
            return (
              <ResizeBox key={index} className="scrollbox-child" onResize={handleResize}>
                {child}
              </ResizeBox>
            );
          })}
        </Scrollbars>
      </Box>
      {showEmptyMessage && (
        <Box className={styles.emptyMessage} p={1}>
          <Text size="small" italic>
            {emptyMessage}
          </Text>
        </Box>
      )}
    </>
  );
};

export default ScrollBox;
